import React, { useState, useEffect, useMemo } from 'react';

const SimpleCryptoLogo = ({ symbol, alt = '', className = '', style = {}, width = 32, height = 32 }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Log when component mounts
  useEffect(() => {
    console.log(`🚀 SimpleCryptoLogo MOUNTED with symbol: "${symbol}"`);
  }, []);
  
  // Symbol mapping for FMP-specific names
  const getFmpSymbol = (symbol) => {
    const symbolMappings = {
      'BTCBUSD': 'BTCUSD',  // Bitcoin BEP2 -> Bitcoin
      'ARBUSD': 'ARBUSD',   // Arbitrum (doesn't exist in FMP - will show fallback)
      'DSHUSD': 'DASHUSD',  // Dash
      'XRPUSD': 'XRPUSD',   // XRP
      'ADAUSD': 'ADAUSD',   // Cardano
      'USDTUSD': 'USDTUSD', // Tether
      'DOGEUSD': 'DOGEUSD', // Dogecoin
      'USDCUSD': 'USDCUSD', // USD Coin
      'XLMUSD': 'XLMUSD',   // Stellar
      'NEARUSD': 'NEARUSD', // NEAR Protocol
      'ALGOUSD': 'ALGOUSD', // Algorand
    };
    
    return symbolMappings[symbol] || symbol;
  };
  
  // Create stable URL that doesn't change on every render
  const fmpUrl = useMemo(() => {
    const fmpSymbol = getFmpSymbol(symbol);
    // Use a stable timestamp based on symbol to avoid constant reloading
    const stableTimestamp = Math.floor(symbol.charCodeAt(0) * 1000 + symbol.length * 100);
    const finalUrl = `https://financialmodelingprep.com/image-stock/${fmpSymbol}.png?v=${stableTimestamp}`;
    console.log(`🔍 SimpleCryptoLogo URL GENERATION:`);
    console.log(`  Input symbol: "${symbol}"`);
    console.log(`  FMP symbol: "${fmpSymbol}"`);
    console.log(`  Final URL: "${finalUrl}"`);
    console.log(`  Timestamp: ${stableTimestamp}`);
    return finalUrl;
  }, [symbol]);
  
  // Reset state when symbol changes
  useEffect(() => {
    console.log(`🔄 SimpleCryptoLogo SYMBOL CHANGE: "${symbol}" - Resetting state`);
    setImageError(false);
    setImageLoaded(false);
  }, [symbol]);
  
  const handleError = () => {
    console.log(`❌ SimpleCryptoLogo ERROR for ${symbol}:`, fmpUrl);
    console.log(`❌ Component state: imageError=${imageError}, imageLoaded=${imageLoaded}`);
    console.log(`❌ Symbol type: ${typeof symbol}, Symbol value: "${symbol}"`);
    setImageError(true);
  };
  
  const handleLoad = () => {
    console.log(`✅ SimpleCryptoLogo LOADED for ${symbol}:`, fmpUrl);
    console.log(`✅ Component state: imageError=${imageError}, imageLoaded=${imageLoaded}`);
    setImageLoaded(true);
  };
  
  // If image failed to load, show fallback
  if (imageError) {
    console.log(`🔄 SimpleCryptoLogo FALLBACK for ${symbol}: Showing colored circle instead of logo`);
    console.log(`🔄 Fallback triggered because imageError=${imageError}`);
    // Generate a consistent color based on symbol hash
    const colors = [
      ['#f43f5e', '#e11d48'], // Rose gradient
      ['#06b6d4', '#0891b2'], // Cyan gradient  
      ['#8b5cf6', '#7c3aed'], // Violet gradient
      ['#10b981', '#059669'], // Emerald gradient
      ['#f59e0b', '#d97706'], // Amber gradient
      ['#ef4444', '#dc2626'], // Red gradient
      ['#3b82f6', '#2563eb'], // Blue gradient
      ['#84cc16', '#65a30d'], // Lime gradient
      ['#f97316', '#ea580c'], // Orange gradient
      ['#ec4899', '#db2777']  // Pink gradient
    ];
    
    // Create a simple hash from symbol for consistent color selection
    let hash = 0;
    for (let i = 0; i < symbol.length; i++) {
      hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % colors.length;
    const [color1, color2] = colors[colorIndex];
    
    // Get display symbol (remove USD suffix)
    const displaySymbol = symbol.replace(/USD$/, '');
    const fallbackText = displaySymbol.substring(0, 3);
    
    return (
      <div
        className={className}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${color1}, ${color2})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          border: '2px solid rgba(255,255,255,0.3)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          textTransform: 'uppercase',
          letterSpacing: '-0.5px',
          ...style
        }}
      >
        {fallbackText}
      </div>
    );
  }
  
  return (
    <img
      src={fmpUrl}
      alt={alt || `${symbol} logo`}
      className={className}
      style={{ width, height, ...style }}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
    />
  );
};

export default SimpleCryptoLogo;
